import { v1CitiesList } from '@on3/api';
import { useToast } from '@on3/ui-lib/index';
import {
  AutoComplete,
  type OptionType,
} from '@on3/ui-lib/src/components/AutoComplete/AutoComplete';
import { externalApi } from '@on3/ui-lib/utils/api';
import { debounce } from '@on3/ui-lib/utils/debounce';
import { filterString } from '@on3/ui-lib/utils/filters';
import { useCallback, useState } from 'react';
import {
  type Control,
  Controller,
  type ControllerRenderProps,
  type DeepMap,
  type FieldError,
  type FieldValues,
  type UseFormStateReturn,
} from 'react-hook-form';

interface CityAutoCompleteProps {
  name: string;
  control?: Control<any, any>;
  label?: string;
  disabled?: boolean;
  limit?: number;
  error?: DeepMap<any, FieldError>;
  value?: OptionType[];
  onChange?: (value: OptionType[]) => void;
}

interface RenderAutoCompleteProps<TFieldValues extends FieldValues> {
  field: ControllerRenderProps<TFieldValues>;
  formState: UseFormStateReturn<TFieldValues>;
}

export const CityAutoComplete = ({
  name,
  control,
  label = 'City',
  disabled = false,
  limit,
  error,
  value,
  onChange,
}: CityAutoCompleteProps) => {
  const { notify } = useToast();
  const [options, setOptions] = useState<OptionType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const handleChange = debounce((term: string) => {
    if (term.length < 3) {
      setOptions([]);

      return;
    }

    setLoading(true);
    handleSearch(term);
  }, 500);

  const handleSearch = async (term: string) => {
    try {
      const response = await v1CitiesList(externalApi, {
        name: term,
      });

      setOptions(
        response.map((result) => ({
          key: result.key,
          label: filterString(
            result.name,
            !!result.state?.abbreviation && `, ${result.state.abbreviation}`,
          ),
        })),
      );
    } catch (e) {
      notify('Failed to fetch locations.', {
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const renderAutoComplete = useCallback(
    ({ field }: RenderAutoCompleteProps<FieldValues>) => {
      return (
        <AutoComplete
          {...field}
          disabled={disabled}
          error={error}
          id={name}
          label={label}
          limit={limit}
          loading={loading}
          onSearch={handleChange}
          options={options}
          value={value}
        />
      );
    },
    [
      disabled,
      error,
      handleChange,
      label,
      limit,
      loading,
      name,
      options,
      value,
    ],
  );

  if (!control) {
    return renderAutoComplete({
      field: { name, value, onChange },
    } as RenderAutoCompleteProps<FieldValues>);
  }

  return (
    <Controller control={control} name={name} render={renderAutoComplete} />
  );
};
